import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import {
  fetchAdSummaryByAd,
  fetchAdSummaryBySubtheme,
  fetchAdSummaryByTheme,
  fetchAdSummaryFilterOptions,
} from '@src/api';
import api from '@src/api/clients';
import { formatDateToYMD } from '@src/utils/dateUtils';

const MAPPING_MODE_OPTIONS = [
  { key: 'all', label: 'All Clients' },
  { key: 'with', label: 'With Mapping' },
  { key: 'without', label: 'Without Mapping' },
];

const VIEW_MODE_OPTIONS = [
  { key: 'ad', label: 'Ads' },
  { key: 'theme', label: 'Themes' },
  { key: 'subtheme', label: 'Subthemes' },
];

const CLIENT_FIELDS = [
  'id',
  'name',
  'slug',
  'parent_company',
  'parent_company_name',
  'mapping_types',
].join(',');

const MAX_CLIENT_META_PAGES = 50;

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  if (!denom) return 0;
  return num / denom;
};

const toOptionObjects = (values = []) =>
  values.map((value) => ({ label: value, value }));

const arraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const meetsThreshold = (value, threshold) => safeNumber(value) >= safeNumber(threshold);
const RECENT_WINDOW_DAYS = 14;
const VIDEO_FORMAT_TOKENS = ['VIDEO', 'REEL', 'STORY', 'MOTION', 'ANIM'];
const DEFAULT_CAMPAIGN_TYPES = ['Broad'];

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;

  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: {
        page,
        page_size: 250,
        ordering: 'name',
        fields: CLIENT_FIELDS,
      },
    });
    const data = response?.data;
    if (Array.isArray(data)) {
      allClients.push(...data);
      break;
    }

    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);

    if (!data?.next) {
      break;
    }
    page += 1;
  }

  return allClients;
}

const METRIC_COLUMNS = [
  { key: 'spend', label: 'Spend', type: 'currency' },
  { key: 'hookRate', label: 'Hook Rate', type: 'ratio' },
  { key: 'ctr', label: 'CTR', type: 'ratio' },
  { key: 'lpvRate', label: 'LPV Rate', type: 'ratio' },
  { key: 'thruplayRate', label: 'Thruplay Rate', type: 'ratio' },
  { key: 'holdRate25', label: 'Hold 25%', type: 'ratio' },
  { key: 'holdRate50', label: 'Hold 50%', type: 'ratio' },
  { key: 'holdRate75', label: 'Hold 75%', type: 'ratio' },
  { key: 'holdRate100', label: 'Hold 100%', type: 'ratio' },
  { key: 'leads', label: 'Leads', type: 'count' },
  { key: 'cpl', label: 'CPL', type: 'currency' },
  { key: 'selfBook', label: 'Self Book', type: 'count' },
  { key: 'cpSelfBook', label: 'CP Self Book', type: 'currency' },
  { key: 'booked', label: 'Booked', type: 'count' },
  { key: 'cpBooked', label: 'CP Booked', type: 'currency' },
  { key: 'setRate', label: 'Set Rate', type: 'ratio' },
  { key: 'scheduled', label: 'Appointments', type: 'count' },
  { key: 'cpScheduled', label: 'CP Appointment', type: 'currency' },
  { key: 'issues', label: 'Issues', type: 'count' },
  { key: 'cpIssue', label: 'CP Issue', type: 'currency' },
  { key: 'issueRate', label: 'Issue Rate', type: 'ratio' },
  { key: 'leadToIssue', label: 'Lead To Issue', type: 'ratio' },
  { key: 'demos', label: 'Demos', type: 'count' },
  { key: 'cpDemo', label: 'CP Demo', type: 'currency' },
  { key: 'demoRate', label: 'Demo Rate', type: 'ratio' },
  { key: 'grossSold', label: 'Gross Sold', type: 'count' },
  { key: 'netSold', label: 'Net Sold', type: 'count' },
  { key: 'leadToSale', label: 'Lead To Sale', type: 'ratio' },
  { key: 'netCloseRate', label: 'Net Close Rate', type: 'ratio' },
  { key: 'netAmount', label: 'Net Amount', type: 'currency' },
  { key: 'avgDeal', label: 'Avg Deal', type: 'currency' },
  { key: 'predictedCostOfMarketing', label: 'Pred. CoM', type: 'ratio' },
  { key: 'effectiveCostOfMarketing', label: 'Decision CoM', type: 'ratio' },
  { key: 'costOfMarketing', label: 'Cost of Marketing', type: 'ratio' },
  { key: 'creativeScore', label: 'Creative Score', type: 'count' },
  { key: 'businessScore', label: 'Business Score', type: 'count' },
  { key: 'overallScore', label: 'Overall Score', type: 'count' },
];

const KPI_OPTIONS = [
  {
    key: 'effectiveCostOfMarketing',
    label: 'Pred./Actual CoM',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.hasBackendIntegration && safeNumber(row.effectiveCostOfMarketing) > 0,
  },
  {
    key: 'costOfMarketing',
    label: 'Cost of Marketing',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.hasBackendIntegration && row.netSold >= 10 && row.netAmount > 0,
  },
  {
    key: 'cpDemo',
    label: 'Cost per Demo',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.hasBackendIntegration && row.demos >= 25,
  },
  {
    key: 'cpIssue',
    label: 'Cost per Issue',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.hasBackendIntegration && row.issues >= 25,
  },
  {
    key: 'cpScheduled',
    label: 'Cost per Appointment',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.hasBackendIntegration && row.scheduled >= 25,
  },
  {
    key: 'cpSelfBook',
    label: 'Cost per Self Book',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.selfBook >= 25,
  },
  {
    key: 'cpl',
    label: 'Cost per Lead',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    isEligible: (row) => row.leads >= 25,
  },
  {
    key: 'ctr',
    label: 'CTR',
    better: 'higher',
    benchmarkScope: 'agency_vertical',
    isEligible: (row) => row.impressions >= 10000,
  },
];

const computeDerivedMetrics = (base = {}) => {
  const spend = safeNumber(base.spend);
  const impressions = safeNumber(base.impressions);
  const clicks = safeNumber(base.clicks);
  const landingPageViews = safeNumber(base.landingPageViews);
  const videoPlays3s = safeNumber(base.videoPlays3s);
  const thruplays = safeNumber(base.thruplays);
  const videoP25 = safeNumber(base.videoP25);
  const videoP50 = safeNumber(base.videoP50);
  const videoP75 = safeNumber(base.videoP75);
  const videoP100 = safeNumber(base.videoP100);
  const leads = safeNumber(base.leads);
  const selfBook = safeNumber(base.selfBook);
  const booked = safeNumber(base.booked);
  const scheduled = safeNumber(base.scheduled);
  const issues = safeNumber(base.issues);
  const demos = safeNumber(base.demos);
  const grossSold = safeNumber(base.grossSold);
  const netSold = safeNumber(base.netSold);
  const netAmount = safeNumber(base.netAmount);

  return {
    spend,
    hookRate: safeDiv(videoPlays3s, impressions),
    ctr: safeDiv(clicks, impressions),
    lpvRate: safeDiv(landingPageViews, clicks),
    thruplayRate: safeDiv(thruplays, impressions),
    holdRate25: safeDiv(videoP25, videoPlays3s),
    holdRate50: safeDiv(videoP50, videoPlays3s),
    holdRate75: safeDiv(videoP75, videoPlays3s),
    holdRate100: safeDiv(videoP100, videoPlays3s),
    leads,
    cpl: safeDiv(spend, leads),
    selfBook,
    cpSelfBook: safeDiv(spend, selfBook),
    booked,
    cpBooked: safeDiv(spend, booked),
    setRate: safeDiv(booked, leads),
    scheduled,
    cpScheduled: safeDiv(spend, scheduled),
    issues,
    cpIssue: safeDiv(spend, issues),
    issueRate: safeDiv(issues, scheduled),
    leadToIssue: safeDiv(issues, leads),
    demos,
    cpDemo: safeDiv(spend, demos),
    demoRate: safeDiv(demos, issues),
    grossSold,
    netSold,
    leadToSale: safeDiv(netSold, leads),
    netCloseRate: safeDiv(netSold, demos),
    netAmount,
    avgDeal: safeDiv(netAmount, netSold),
    costOfMarketing: safeDiv(spend, netAmount),
  };
};

const renderMetricValue = (value, type) => {
  if (type === 'currency') {
    return <DollarFormatter value={value} />;
  }
  if (type === 'ratio') {
    return <PercentageFormatter value={value} />;
  }
  return safeNumber(value).toLocaleString();
};

const getDimensionColumns = (viewMode) => {
  if (viewMode === 'theme') {
    return [
      { key: 'themeName', label: 'Theme', minWidth: 220 },
      { key: 'clientCount', label: 'Clients', minWidth: 100, type: 'count' },
      { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
      { key: 'maturityLabel', label: 'Maturity', minWidth: 160 },
    ];
  }
  if (viewMode === 'subtheme') {
    return [
      { key: 'themeName', label: 'Theme', minWidth: 220 },
      { key: 'subthemeName', label: 'Subtheme', minWidth: 220 },
      { key: 'clientCount', label: 'Clients', minWidth: 100, type: 'count' },
      { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
      { key: 'maturityLabel', label: 'Maturity', minWidth: 160 },
    ];
  }
  return [
    { key: 'clientName', label: 'Client', minWidth: 220, sticky: true, stickyLeft: 0 },
    { key: 'verticalName', label: 'Vertical', minWidth: 180, sticky: true, stickyLeft: 220 },
    { key: 'adName', label: 'Ad', minWidth: 264, sticky: true, stickyLeft: 400, truncate: true },
    { key: 'previewAction', label: 'Preview', minWidth: 110, stickyLeft: 664 },
    { key: 'facebookFormat', label: 'Format', minWidth: 120, stickyLeft: 774 },
    {
      key: 'stageLabel',
      label: 'Stage',
      minWidth: 150,
      helpText: 'The current evaluation stage for this ad based on the deepest KPI threshold it has reached.',
    },
    { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
    {
      key: 'spendShareLabel',
      label: 'Spend Share',
      minWidth: 120,
      helpText: 'Share of spend within this client and vertical in the current date range.',
    },
    {
      key: 'primaryKpiLabel',
      label: 'Primary KPI',
      minWidth: 150,
      helpText: 'The most appropriate KPI to judge this ad right now given its maturity.',
    },
    {
      key: 'predictedCostOfMarketingLabel',
      label: 'Pred. CoM',
      minWidth: 140,
      helpText: 'Projected Cost of Marketing based on the ad’s current stage and comparable funnel conversion rates.',
    },
    {
      key: 'effectiveCostOfMarketingLabel',
      label: 'Decision CoM',
      minWidth: 150,
      helpText: 'Actual Cost of Marketing for mature ads, otherwise the predicted Cost of Marketing used for cross-stage comparison.',
    },
    {
      key: 'pausePriorityLabel',
      label: 'Pause Priority',
      minWidth: 140,
      helpText: 'Higher when an ad looks weak, confidence is meaningful, and enough spend is at risk to justify action.',
    },
    {
      key: 'buildPriorityLabel',
      label: 'Build Priority',
      minWidth: 140,
      helpText: 'Higher when an ad looks like a winner worth reusing for new creative variations.',
    },
    {
      key: 'creativeVerdict',
      label: 'Creative Verdict',
      minWidth: 150,
      helpText: 'Quick read on whether the creative looks winning, promising, mixed, weak, or still too early.',
    },
    {
      key: 'overallRecommendation',
      label: 'Overall Rec.',
      minWidth: 150,
      helpText: 'Suggested next action based on the full selected date range.',
    },
    {
      key: 'whyLabel',
      label: 'Why',
      minWidth: 260,
      helpText: 'Short explanation for the current recommendation.',
      truncate: true,
    },
  ];
};

const getRowLabel = (row, viewMode) => {
  if (viewMode === 'theme') return row.themeName || 'Unparsed Theme';
  if (viewMode === 'subtheme') return row.subthemeName || row.themeName || 'Unparsed Subtheme';
  return row.adName || 'Unnamed Ad';
};

const getStatusPillStyles = (tone) => {
  const palette = {
    success: { bg: '#dcfce7', fg: '#166534' },
    warning: { bg: '#fef3c7', fg: '#92400e' },
    danger: { bg: '#fee2e2', fg: '#b91c1c' },
    info: { bg: '#dbeafe', fg: '#1d4ed8' },
    neutral: { bg: '#e5e7eb', fg: '#374151' },
    violet: { bg: '#ede9fe', fg: '#6d28d9' },
  };
  return palette[tone] || palette.neutral;
};

const renderStatusPill = (label, tone = 'neutral') => {
  const colors = getStatusPillStyles(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.1,
        py: 0.45,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {label || '-'}
    </Box>
  );
};

const getRatioTone = (value) => {
  const numeric = safeNumber(value);
  if (!numeric) return 'neutral';
  if (numeric <= 0.2) return 'success';
  if (numeric <= 0.35) return 'warning';
  return 'danger';
};

const getPriorityTone = (value) => {
  const numeric = safeNumber(value);
  if (numeric >= 70) return 'danger';
  if (numeric >= 40) return 'warning';
  return 'neutral';
};

const getBuildTone = (value) => {
  const numeric = safeNumber(value);
  if (numeric >= 70) return 'success';
  if (numeric >= 40) return 'info';
  return 'neutral';
};

const getConfidenceTone = (label) => {
  if (label === 'High') return 'success';
  if (label === 'Medium') return 'warning';
  return 'neutral';
};

const getRecommendationTone = (label) => {
  if (label === 'Pause') return 'danger';
  if (label === 'Keep Running') return 'success';
  if (label === 'Test Longer') return 'warning';
  if (label === 'Watch') return 'info';
  return 'neutral';
};

const getVerdictTone = (label) => {
  if (label === 'Winning') return 'success';
  if (label === 'Promising') return 'info';
  if (label === 'Weak') return 'danger';
  if (label === 'Mixed') return 'warning';
  return 'neutral';
};

const getStageTone = (label) => {
  if (label === 'Sales Stage') return 'success';
  if (label === 'Demo Stage' || label === 'Issue Stage' || label === 'Appointment Stage') return 'info';
  if (label === 'Lead Stage' || label === 'Self Book Stage') return 'warning';
  if (label === 'Creative Signal') return 'violet';
  return 'neutral';
};

const getFormatTone = (label) => {
  const format = String(label || '').toUpperCase();
  if (VIDEO_FORMAT_TOKENS.some((token) => format.includes(token))) return 'info';
  if (!format || format === 'UNKNOWN') return 'neutral';
  return 'violet';
};

const renderDimensionValue = (column, row, onPreviewOpen) => {
  if (column.key === 'previewAction') {
    return row.thumbnailUrl || row.imageUrl || row.headline || row.primaryText ? (
      <Button size="small" variant="outlined" onClick={() => onPreviewOpen(row)}>
        Preview
      </Button>
    ) : (
      <Typography variant="body2" color="text.secondary">-</Typography>
    );
  }

  if (column.key === 'facebookFormat') return renderStatusPill(row[column.key] || 'Unknown', getFormatTone(row[column.key]));
  if (column.key === 'stageLabel') return renderStatusPill(row[column.key] || '-', getStageTone(row[column.key]));
  if (column.key === 'confidenceLabel') return renderStatusPill(row[column.key] || '-', getConfidenceTone(row[column.key]));
  if (column.key === 'spendShareLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.spendShare));
  if (column.key === 'primaryKpiLabel') return renderStatusPill(row[column.key] || '-', 'neutral');
  if (column.key === 'predictedCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.predictedCostOfMarketing));
  if (column.key === 'effectiveCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.effectiveCostOfMarketing));
  if (column.key === 'pausePriorityLabel') return renderStatusPill(row[column.key] || '-', getPriorityTone(row.pausePriority));
  if (column.key === 'buildPriorityLabel') return renderStatusPill(row[column.key] || '-', getBuildTone(row.buildPriority));
  if (column.key === 'creativeVerdict') return renderStatusPill(row[column.key] || '-', getVerdictTone(row[column.key]));
  if (column.key === 'overallRecommendation') return renderStatusPill(row[column.key] || '-', getRecommendationTone(row[column.key]));
  if (column.type === 'count') return safeNumber(row[column.key]).toLocaleString();
  if (column.truncate) {
    return (
      <Typography noWrap sx={{ maxWidth: column.minWidth }}>
        {row[column.key] || '-'}
      </Typography>
    );
  }
  return row[column.key] || '-';
};

const getRankedRows = (rows, kpiOption, direction) => {
  if (!kpiOption) return [];

  const eligibleRows = rows
    .filter((row) => row.spend > 0)
    .filter((row) => kpiOption.isEligible(row));

  const sorted = [...eligibleRows].sort((left, right) => {
    const leftValue = safeNumber(left[kpiOption.key]);
    const rightValue = safeNumber(right[kpiOption.key]);

    if (kpiOption.better === 'lower') {
      return direction === 'best' ? leftValue - rightValue : rightValue - leftValue;
    }
    return direction === 'best' ? rightValue - leftValue : leftValue - rightValue;
  });

  return sorted.slice(0, 5);
};

const getReportFetcher = (viewMode) => {
  if (viewMode === 'theme') return fetchAdSummaryByTheme;
  if (viewMode === 'subtheme') return fetchAdSummaryBySubtheme;
  return fetchAdSummaryByAd;
};

const getAdAgeDays = (createdTime, referenceDate) => {
  if (!createdTime || !referenceDate) return null;
  const createdAt = new Date(createdTime);
  if (Number.isNaN(createdAt.getTime())) return null;
  const ref = new Date(referenceDate);
  ref.setHours(23, 59, 59, 999);
  const diffMs = ref.getTime() - createdAt.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const formatAdAge = (ageDays) => {
  if (ageDays == null) return 'Unknown';
  if (ageDays < 7) return `${ageDays}d`;
  if (ageDays < 30) return `${Math.floor(ageDays / 7)}w`;
  return `${Math.floor(ageDays / 30)}mo`;
};

const getLifecycleLabel = ({ ageDays, spend, impressions, leads, issues, demos, netSold }) => {
  if (ageDays == null) return 'Unknown';
  if (ageDays <= 14 && spend < 100 && impressions < 5000) return 'New';
  if (spend > 100 || impressions > 5000 || leads > 0 || issues > 0 || demos > 0 || netSold > 0) {
    return 'Active';
  }
  if (ageDays > 30 && spend < 25 && impressions < 1000) return 'Deprioritized';
  if (ageDays > 14) return 'Dormant';
  return 'New';
};

const KPI_STAGE_RULES = [
  {
    stageLabel: 'Sales Stage',
    thresholdLabel: '10 Net Sales',
    key: 'costOfMarketing',
    label: 'Cost of Marketing',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.netSold >= 10 && row.netAmount > 0,
  },
  {
    stageLabel: 'Demo Stage',
    thresholdLabel: '25 Demos',
    key: 'cpDemo',
    label: 'Cost Per Demo',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.demos >= 25,
  },
  {
    stageLabel: 'Issue Stage',
    thresholdLabel: '25 Issues',
    key: 'cpIssue',
    label: 'Cost Per Issue',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.issues >= 25,
  },
  {
    stageLabel: 'Appointment Stage',
    thresholdLabel: '25 Appointments',
    key: 'cpScheduled',
    label: 'Cost Per Appointment',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.scheduled >= 25,
  },
  {
    stageLabel: 'Self Book Stage',
    thresholdLabel: '25 Self Books',
    key: 'cpSelfBook',
    label: 'Cost Per Self Book',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.selfBook >= 25,
  },
  {
    stageLabel: 'Lead Stage',
    thresholdLabel: '25 Leads',
    key: 'cpl',
    label: 'CPL',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.leads >= 25,
  },
  {
    stageLabel: 'Creative Signal',
    thresholdLabel: '10K Impressions',
    key: 'ctr',
    label: 'CTR',
    better: 'higher',
    benchmarkScope: 'agency_vertical',
    qualifies: (row) => row.impressions >= 10000,
  },
];

const getKpiStage = (row) => KPI_STAGE_RULES.find((rule) => rule.qualifies(row)) || null;

const getFunnelBenchmarks = (rows) => {
  const totals = rows.reduce(
    (acc, row) => {
      acc.leads += safeNumber(row.leads);
      acc.scheduled += safeNumber(row.scheduled);
      acc.issues += safeNumber(row.issues);
      acc.demos += safeNumber(row.demos);
      acc.netSold += safeNumber(row.netSold);
      acc.netAmount += safeNumber(row.netAmount);
      return acc;
    },
    {
      leads: 0,
      scheduled: 0,
      issues: 0,
      demos: 0,
      netSold: 0,
      netAmount: 0,
    }
  );

  return {
    leadToAppointment: safeDiv(totals.scheduled, totals.leads),
    appointmentToIssue: safeDiv(totals.issues, totals.scheduled),
    issueToDemo: safeDiv(totals.demos, totals.issues),
    demoToNetSale: safeDiv(totals.netSold, totals.demos),
    expectedAvgNetAmount: safeDiv(totals.netAmount, totals.netSold),
  };
};

const hasUsableBenchmarks = (benchmarks) => (
  safeNumber(benchmarks.leadToAppointment) > 0
  && safeNumber(benchmarks.appointmentToIssue) > 0
  && safeNumber(benchmarks.issueToDemo) > 0
  && safeNumber(benchmarks.demoToNetSale) > 0
  && safeNumber(benchmarks.expectedAvgNetAmount) > 0
);

const getPredictedCostPerNetSale = (row, benchmarks) => {
  if (!row?.hasBackendIntegration) return null;
  if (!hasUsableBenchmarks(benchmarks)) return null;

  if (row.demos >= 25 && benchmarks.demoToNetSale > 0) {
    return safeDiv(row.cpDemo, benchmarks.demoToNetSale);
  }

  if (row.issues >= 25) {
    return safeDiv(row.cpIssue, benchmarks.issueToDemo * benchmarks.demoToNetSale);
  }

  if (row.scheduled >= 25) {
    return safeDiv(
      row.cpScheduled,
      benchmarks.appointmentToIssue * benchmarks.issueToDemo * benchmarks.demoToNetSale
    );
  }

  if (row.selfBook >= 25 || row.leads >= 25) {
    return safeDiv(
      row.cpl,
      benchmarks.leadToAppointment
        * benchmarks.appointmentToIssue
        * benchmarks.issueToDemo
        * benchmarks.demoToNetSale
    );
  }

  return null;
};

const getPredictedCostOfMarketing = (row, benchmarks) => {
  const predictedCostPerNetSale = getPredictedCostPerNetSale(row, benchmarks);
  if (!predictedCostPerNetSale || !benchmarks.expectedAvgNetAmount) return null;
  return safeDiv(predictedCostPerNetSale, benchmarks.expectedAvgNetAmount);
};

const getMaturityLabel = (row) => {
  const stage = getKpiStage(row);
  if (stage) return stage.stageLabel;
  return 'Insufficient Data';
};

const getPrimaryKpi = (row) => {
  const stage = getKpiStage(row);
  if (stage) {
    return {
      key: stage.key,
      label: stage.label,
      better: stage.better,
      benchmarkScope: stage.benchmarkScope,
      isEligible: (candidate) => {
        if (stage.key === 'costOfMarketing') return candidate.hasBackendIntegration && candidate.netSold >= 10 && candidate.netAmount > 0;
        if (stage.key === 'cpDemo') return candidate.hasBackendIntegration && candidate.demos >= 25;
        if (stage.key === 'cpIssue') return candidate.hasBackendIntegration && candidate.issues >= 25;
        if (stage.key === 'cpScheduled') return candidate.hasBackendIntegration && candidate.scheduled >= 25;
        if (stage.key === 'cpSelfBook') return candidate.selfBook >= 25;
        if (stage.key === 'cpl') return candidate.leads >= 25;
        if (stage.key === 'ctr') return candidate.impressions >= 10000;
        return false;
      },
    };
  }
  return null;
};

const getBenchmarkDelta = (row, peerRows, kpi) => {
  if (!kpi || !peerRows.length) return null;
  const eligiblePeers = peerRows.filter((peer) => kpi.isEligible(peer));
  if (!eligiblePeers.length) return null;
  const peerAverage = eligiblePeers.reduce((sum, peer) => sum + safeNumber(peer[kpi.key]), 0) / eligiblePeers.length;
  if (!peerAverage) return null;
  return safeDiv(safeNumber(row[kpi.key]) - peerAverage, peerAverage);
};

const formatDeltaLabel = (delta) => {
  if (delta == null) return '-';
  const percentage = `${Math.abs(delta * 100).toFixed(0)}%`;
  return delta >= 0 ? `+${percentage}` : `-${percentage}`;
};

const formatPercentLabel = (value) => {
  if (value == null) return '-';
  return `${Math.round(safeNumber(value) * 100)}%`;
};

const formatPriorityLabel = (value) => `${clampScore(value)}/100`;

const getDirectionalDelta = (delta, better = 'higher') => {
  if (delta == null) return 0;
  return better === 'lower' ? -safeNumber(delta) : safeNumber(delta);
};

const isVideoCreative = (row = {}) => {
  const format = String(row.facebookFormat || '').toUpperCase();
  if (VIDEO_FORMAT_TOKENS.some((token) => format.includes(token))) {
    return true;
  }
  return safeNumber(row.videoPlays3s) > 0 || safeNumber(row.thruplays) > 0;
};

const normalizeCampaignType = (value = '') => {
  const lowered = String(value).trim().toLowerCase();
  if (!lowered) return 'Unknown';
  if (lowered.includes('broad')) return 'Broad';
  if (lowered.includes('local') || lowered.includes('county')) return 'Local';
  return 'Unknown';
};

const getRecommendation = (row, primaryKpi, benchmarkDelta) => {
  if (!primaryKpi) return 'Test Longer';
  if (row.lifecycleLabel === 'Deprioritized') return 'Dormant';
  if (row.maturityLabel === 'Insufficient Data') return 'Test Longer';
  if (benchmarkDelta == null) return 'Watch';

  if (primaryKpi.better === 'lower') {
    if (benchmarkDelta <= -0.15) return 'Keep Running';
    if (benchmarkDelta >= 0.2) return 'Pause';
  } else {
    if (benchmarkDelta >= 0.15) return 'Keep Running';
    if (benchmarkDelta <= -0.2) return 'Pause';
  }

  if (Math.abs(benchmarkDelta) <= 0.1) return 'Keep Running';
  return 'Watch';
};

const getPeerRankLabel = (row, peerRows, kpi) => {
  if (!kpi || !peerRows.length) return '-';
  const eligiblePeers = peerRows.filter((peer) => kpi.isEligible(peer));
  if (!eligiblePeers.length) return '-';

  const comparator = kpi.better === 'lower'
    ? (peer) => safeNumber(peer[kpi.key]) >= safeNumber(row[kpi.key])
    : (peer) => safeNumber(peer[kpi.key]) <= safeNumber(row[kpi.key]);
  const beatingCount = eligiblePeers.filter(comparator).length;
  const percentile = Math.round((beatingCount / eligiblePeers.length) * 100);
  if (percentile >= 90) return 'Top 10%';
  if (percentile >= 75) return 'Top Quartile';
  if (percentile <= 25) return 'Bottom Quartile';
  return 'Middle Pack';
};

const getStabilityLabel = (recentRecommendation, overallRecommendation, trendLabel) => {
  if (recentRecommendation === 'Keep Running' && overallRecommendation === 'Keep Running') return 'Consistent Winner';
  if (recentRecommendation === 'Pause' && overallRecommendation === 'Pause') return 'Consistent Weak';
  if (recentRecommendation === 'Keep Running' && overallRecommendation !== 'Keep Running') return 'Emerging';
  if (recentRecommendation === 'Pause' && overallRecommendation !== 'Pause') return 'Fading';
  if (trendLabel === 'Improving') return 'Building';
  if (trendLabel === 'Declining') return 'Slipping';
  return 'Mixed';
};

const clampScore = (value) => Math.max(0, Math.min(100, Math.round(value)));

const scoreHigherIsBetter = (value, target, max = 1.25) =>
  clampScore((Math.min(safeDiv(value, target), max) / max) * 100);

const scoreLowerIsBetter = (value, target, max = 2) => {
  if (!value) return 0;
  const inverted = safeDiv(target, value);
  return clampScore((Math.min(inverted, max) / max) * 100);
};

const getCreativeScore = (row) => {
  const parts = [
    scoreHigherIsBetter(row.ctr, 0.0125),
    scoreHigherIsBetter(row.lpvRate, 0.45),
  ];

  if (isVideoCreative(row)) {
    parts.push(
      scoreHigherIsBetter(row.hookRate, 0.25),
      scoreHigherIsBetter(row.thruplayRate, 0.06),
      scoreHigherIsBetter(row.holdRate50, 0.3)
    );
  }

  return clampScore(parts.reduce((sum, value) => sum + value, 0) / parts.length);
};

const getBusinessScore = (row) => {
  const parts = [
    scoreLowerIsBetter(row.cpl, 200),
    scoreLowerIsBetter(row.cpSelfBook, 400),
    scoreLowerIsBetter(row.cpScheduled, 600),
    scoreLowerIsBetter(row.cpIssue, 1000),
    scoreLowerIsBetter(row.cpDemo, 2500),
    scoreLowerIsBetter(row.effectiveCostOfMarketing || row.costOfMarketing, 0.2),
  ];
  return clampScore(parts.reduce((sum, value) => sum + value, 0) / parts.length);
};

const getOverallScore = (row, creativeScore, businessScore) => {
  if (row.maturityLabel === 'Insufficient Data') return creativeScore;
  if (row.maturityLabel === 'Creative Signal') return clampScore(creativeScore * 0.8);
  return clampScore((creativeScore * 0.4) + (businessScore * 0.6));
};

const getBenchmarkAdjustedScore = (baseScore, delta, better = 'higher') => {
  if (delta == null) return clampScore(baseScore);
  const normalizedDelta = better === 'lower' ? -safeNumber(delta) : safeNumber(delta);
  const adjustment = Math.max(-20, Math.min(20, normalizedDelta * 40));
  return clampScore(baseScore + adjustment);
};

const getConfidenceScore = (row) => {
  let score = 0;

  if (row.impressions >= 25000) score += 20;
  else if (row.impressions >= 10000) score += 16;
  else if (row.impressions >= 5000) score += 12;
  else if (row.impressions >= 1000) score += 8;
  else if (row.impressions > 0) score += 4;

  if (row.leads >= 25) score += 20;
  else if (row.leads >= 15) score += 16;
  else if (row.leads >= 10) score += 12;
  else if (row.leads >= 5) score += 8;
  else if (row.leads > 0) score += 4;

  if (row.issues >= 25) score += 20;
  else if (row.issues >= 8) score += 16;
  else if (row.issues >= 5) score += 12;
  else if (row.issues >= 2) score += 8;
  else if (row.issues > 0) score += 4;

  if (row.demos >= 25) score += 20;
  else if (row.demos >= 4) score += 16;
  else if (row.demos >= 2) score += 12;
  else if (row.demos > 0) score += 6;

  if (row.netSold >= 10) score += 20;
  else if (row.netSold >= 2) score += 16;
  else if (row.netSold >= 1) score += 10;
  else if (row.netAmount >= 5000) score += 8;

  return clampScore(score);
};

const getConfidenceLabel = (score) => {
  if (score >= 75) return 'High';
  if (score >= 45) return 'Medium';
  return 'Low';
};

const getTrendLabel = (overallDelta, recentDelta, better = 'higher') => {
  if (overallDelta == null || recentDelta == null) return 'Flat';
  const directionalRecent = better === 'lower' ? -safeNumber(recentDelta) : safeNumber(recentDelta);
  const directionalOverall = better === 'lower' ? -safeNumber(overallDelta) : safeNumber(overallDelta);
  const change = directionalRecent - directionalOverall;
  if (change >= 0.1) return 'Improving';
  if (change <= -0.1) return 'Declining';
  return 'Flat';
};

const getWhyLabel = ({
  row,
  primaryKpi,
  benchmarkDelta,
  benchmarkScope,
  recommendation,
  diagnosticLabel,
  maturityLabel,
  spendShare,
  confidenceLabel,
}) => {
  if (!primaryKpi) return 'Not enough mature data yet.';
  if (recommendation === 'Test Longer') return 'Not enough mature data yet.';
  if (recommendation === 'Dormant') return 'Older ad with very low recent delivery.';
  if (!row?.hasBackendIntegration && ['Lead Stage', 'Self Book Stage'].includes(maturityLabel)) {
    return 'This client has no backend integration, so evaluation stops at lead and self-book stages.';
  }
  if (benchmarkDelta == null) return 'No valid peer benchmark in the current scope.';

  const scopeLabel = benchmarkScope === 'agency_vertical' ? 'agency vertical' : 'client vertical';
  const deltaText = `${Math.abs(safeNumber(benchmarkDelta) * 100).toFixed(0)}%`;
  const betterIsHigher = primaryKpi.better === 'higher';
  const isWinning = betterIsHigher ? benchmarkDelta >= 0 : benchmarkDelta <= 0;
  const relativeText = isWinning ? 'above' : 'below';

  if (maturityLabel === 'Creative Signal' && primaryKpi.key === 'ctr') {
    if (isVideoCreative(row)) {
      return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on CTR by ${deltaText}; Hook Rate is the secondary video diagnostic.`;
    }
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on CTR by ${deltaText}.`;
  }
  if (primaryKpi.key === 'effectiveCostOfMarketing') {
    const spendText = safeNumber(spendShare) >= 0.1 ? ` with ${formatPercentLabel(spendShare)} of spend` : '';
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on projected/actual Cost of Marketing by ${deltaText}${spendText}.`;
  }
  if (diagnosticLabel === 'Funnel Problem') {
    return `Creative is engaging, but backend quality is ${relativeText === 'above' ? 'still mixed' : 'lagging'} peers.`;
  }
  if (diagnosticLabel === 'Creative Problem') {
    return `Backend outcomes are acceptable, but top-funnel creative engagement trails peers.`;
  }
  if (diagnosticLabel === 'Backend Problem') {
    return `Ads are reaching demos, but sales conversion is still weak.`;
  }
  if (recommendation === 'Pause' && confidenceLabel === 'High') {
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on ${primaryKpi.label} by ${deltaText} with high confidence.`;
  }
  return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on ${primaryKpi.label} by ${deltaText}.`;
};

const getCreativeVerdict = (row, creativeScore, overallScore) => {
  if (row.maturityLabel === 'Insufficient Data') return 'Needs More Data';
  if (overallScore >= 75) return 'Winning';
  if (creativeScore >= 65 || overallScore >= 60) return 'Promising';
  if (overallScore <= 40) return 'Weak';
  return 'Mixed';
};

const getDiagnosticLabel = (row, creativeScore, businessScore) => {
  if (row.maturityLabel === 'Insufficient Data') return 'Needs More Data';
  if (creativeScore >= 65 && businessScore < 45) return 'Funnel Problem';
  if (creativeScore < 45 && businessScore >= 55) return 'Creative Problem';
  if (row.netSold === 0 && row.demos > 0) return 'Backend Problem';
  if (creativeScore >= 65 && businessScore >= 65) return 'Balanced Winner';
  return 'Mixed Signals';
};

const getPausePriority = ({
  overallRecommendation,
  recentRecommendation,
  creativeVerdict,
  diagnosticLabel,
  confidenceScore,
  spendShare,
  directionalDelta,
}) => {
  let score = 0;

  if (overallRecommendation === 'Pause') score += 42;
  else if (overallRecommendation === 'Watch') score += 12;
  else if (overallRecommendation === 'Dormant') score += 8;

  if (recentRecommendation === 'Pause') score += 12;
  else if (recentRecommendation === 'Watch') score += 4;

  if (directionalDelta < 0) {
    score += Math.min(24, Math.abs(directionalDelta) * 55);
  }

  if (creativeVerdict === 'Weak') score += 10;
  if (diagnosticLabel === 'Funnel Problem' || diagnosticLabel === 'Backend Problem') score += 6;

  score += safeNumber(confidenceScore) * 0.18;
  score += Math.min(18, safeNumber(spendShare) * 120);

  if (overallRecommendation === 'Test Longer') score *= 0.45;
  if (overallRecommendation === 'Keep Running') score *= 0.15;

  return clampScore(score);
};

const getBuildPriority = ({
  overallRecommendation,
  recentRecommendation,
  creativeVerdict,
  confidenceScore,
  spendShare,
  directionalDelta,
  stageLabel,
}) => {
  let score = 0;

  if (overallRecommendation === 'Keep Running') score += 42;
  else if (overallRecommendation === 'Watch') score += 6;

  if (recentRecommendation === 'Keep Running') score += 12;
  else if (recentRecommendation === 'Watch') score += 4;

  if (directionalDelta > 0) {
    score += Math.min(24, directionalDelta * 55);
  }

  if (creativeVerdict === 'Winning') score += 12;
  else if (creativeVerdict === 'Promising') score += 8;

  score += safeNumber(confidenceScore) * 0.18;
  score += Math.min(12, safeNumber(spendShare) * 80);

  if (stageLabel === 'Creative Signal') score *= 0.8;
  if (overallRecommendation === 'Pause' || overallRecommendation === 'Dormant') score *= 0.1;

  return clampScore(score);
};

const aggregatePatternLeaders = (rows, key) => {
  const grouped = new Map();
  rows.forEach((row) => {
    const groupKey = row[key];
    if (!groupKey || groupKey === '-') return;
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        name: groupKey,
        count: 0,
        spend: 0,
        creativeScore: 0,
        businessScore: 0,
        overallScore: 0,
      });
    }
    const bucket = grouped.get(groupKey);
    bucket.count += 1;
    bucket.spend += safeNumber(row.spend);
    bucket.creativeScore += safeNumber(row.creativeScore);
    bucket.businessScore += safeNumber(row.businessScore);
    bucket.overallScore += safeNumber(row.overallScore);
  });

  return Array.from(grouped.values()).map((bucket) => ({
    ...bucket,
    creativeScore: clampScore(bucket.creativeScore / bucket.count),
    businessScore: clampScore(bucket.businessScore / bucket.count),
    overallScore: clampScore(bucket.overallScore / bucket.count),
  }));
};

const getBestComponentLeaders = (rows, key, limit = 5) =>
  aggregatePatternLeaders(rows, key)
    .sort((a, b) => b.overallScore - a.overallScore)
    .slice(0, limit);

const getRecentStartDate = (startDate, endDate) => {
  const recentStart = new Date(endDate);
  recentStart.setDate(recentStart.getDate() - (RECENT_WINDOW_DAYS - 1));
  return recentStart > startDate ? recentStart : startDate;
};

const getLastThirtyDays = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return [start, end];
};

export default function ClientPerformanceAggregator() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [recentRows, setRecentRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedParentCompanies, setSelectedParentCompanies] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [mappingMode, setMappingMode] = React.useState('with');
  const [viewMode, setViewMode] = React.useState('ad');
  const [kpiMetric, setKpiMetric] = React.useState('effectiveCostOfMarketing');
  const [selectedCampaignTypes, setSelectedCampaignTypes] = React.useState(DEFAULT_CAMPAIGN_TYPES);
  const [selectedVerticals, setSelectedVerticals] = React.useState([]);
  const [selectedThemes, setSelectedThemes] = React.useState([]);
  const [selectedSubthemes, setSelectedSubthemes] = React.useState([]);
  const [selectedAdTypes, setSelectedAdTypes] = React.useState([]);
  const [selectedNotes, setSelectedNotes] = React.useState([]);
  const [previewRow, setPreviewRow] = React.useState(null);
  const [minSpend, setMinSpend] = React.useState('0');
  const [minImpressions, setMinImpressions] = React.useState('0');
  const [minLeads, setMinLeads] = React.useState('0');
  const [minIssues, setMinIssues] = React.useState('0');
  const [minDemos, setMinDemos] = React.useState('0');
  const [minNetSales, setMinNetSales] = React.useState('0');
  const [filterOptions, setFilterOptions] = React.useState({
    campaignTypes: [],
    verticals: [],
    themes: [],
    subthemes: [],
    adTypes: [],
    notes: [],
  });

  React.useEffect(() => {
    let isMounted = true;

    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        const normalized = clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          slug: client.slug || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        }));
        setClientMetadata(normalized);
      } catch (err) {
        console.error('Failed to load client metadata:', err);
        if (!isMounted) return;
        setClientMetadata([]);
        setError('Unable to load client metadata.');
      } finally {
        if (isMounted) {
          setLoadingClientMeta(false);
        }
      }
    };

    loadClients();
    return () => {
      isMounted = false;
    };
  }, []);

  const clientMetadataById = React.useMemo(() => {
    const next = new Map();
    clientMetadata.forEach((client) => {
      next.set(client.id, client);
    });
    return next;
  }, [clientMetadata]);

  const eligibleClients = React.useMemo(() => {
    return clientMetadata.filter((client) => {
      if (mappingMode === 'with' && !client.hasMapping) return false;
      if (mappingMode === 'without' && client.hasMapping) return false;
      if (selectedParentCompanies.length > 0 && !selectedParentCompanies.includes(client.parentCompanyId)) return false;
      return true;
    });
  }, [clientMetadata, mappingMode, selectedParentCompanies]);

  const parentCompanyOptions = React.useMemo(() => {
    const deduped = new Map();
    eligibleClients.forEach((client) => {
      if (!deduped.has(client.parentCompanyId)) {
        deduped.set(client.parentCompanyId, {
          id: client.parentCompanyId,
          name: client.parentCompanyName,
        });
      }
    });
    return Array.from(deduped.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  const clientOptions = React.useMemo(() => {
    return eligibleClients
      .map((client) => ({ id: client.id, name: client.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  React.useEffect(() => {
    const allowed = new Set(parentCompanyOptions.map((option) => option.id));
    setSelectedParentCompanies((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [parentCompanyOptions]);

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((option) => option.id));
    setSelectedClients((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [clientOptions]);

  React.useEffect(() => {
    const normalized = selectedCampaignTypes.map(normalizeCampaignType);
    if (normalized.length !== 1) return;

    if (normalized[0] === 'Local' && viewMode === 'ad') {
      setViewMode('subtheme');
      return;
    }

    if (normalized[0] === 'Broad' && viewMode !== 'ad') {
      setViewMode('ad');
    }
  }, [selectedCampaignTypes, viewMode]);

  const effectiveClientIds = React.useMemo(() => {
    const scopedClients = selectedClients.length > 0
      ? eligibleClients.filter((client) => selectedClients.includes(client.id))
      : eligibleClients;

    if (
      mappingMode === 'all'
      && selectedParentCompanies.length === 0
      && selectedClients.length === 0
    ) {
      return [];
    }

    return scopedClients.map((client) => client.id);
  }, [eligibleClients, mappingMode, selectedClients, selectedParentCompanies]);

  const mapSummaryRows = React.useCallback((summaryRows, referenceDate, currentViewMode) => (
    summaryRows
      .map((row = {}) => {
        const clientId = row.client_id ? String(row.client_id) : '';
        const clientMeta = clientId ? clientMetadataById.get(clientId) : null;

        const baseRow = {
          uuid: row.uuid || `${currentViewMode}-${row.theme || ''}-${row.subtheme || ''}-${row.ad_id || row.ad_name || row.client_id || ''}`,
          clientId,
          clientName: clientMeta?.name || row.client_name || row.client__name || '',
          parentCompanyId: clientMeta?.parentCompanyId || '__none__',
          parentCompanyName: clientMeta?.parentCompanyName || 'No Parent Company',
          hasMapping: Boolean(clientMeta?.hasMapping),
          hasBackendIntegration: Boolean(clientMeta?.hasMapping),
          verticalName: row.vertical || '',
          campaignType: normalizeCampaignType(row.campaign_type || ''),
          themeName: row.theme || '',
          subthemeName: row.subtheme || '',
          adType: row.ad_type || '',
          adName: row.ad_name || row.ad || '',
          facebookFormat: row.facebook_format || '',
          headline: row.headline || '',
          primaryText: row.primary_text || '',
          description: row.description || '',
          destinationUrl: row.destination_url || '',
          ctaType: row.cta_type || '',
          thumbnailUrl: row.thumbnail_url || '',
          imageUrl: row.image_url || '',
          videoId: row.video_id || '',
          clientCount: safeNumber(row.client_count),
          spend: safeNumber(row.total_spend_sum),
          impressions: safeNumber(row.impressions_sum),
          clicks: safeNumber(row.clicks_sum),
          landingPageViews: safeNumber(row.landing_page_views_sum),
          videoPlays3s: safeNumber(row.video_plays_3s_sum),
          thruplays: safeNumber(row.thruplays_sum),
          videoP25: safeNumber(row.video_p25_sum),
          videoP50: safeNumber(row.video_p50_sum),
          videoP75: safeNumber(row.video_p75_sum),
          videoP100: safeNumber(row.video_p100_sum),
          leads: safeNumber(row.total_leads_sum),
          selfBook: safeNumber(row.self_books_sum),
          booked: safeNumber(row.apps_booked_sum || row.booked_sum),
          scheduled: safeNumber(row.scheduled_sum),
          issues: safeNumber(row.issue_sum || row.issues_sum),
          demos: safeNumber(row.demo_sum || row.demos_sum),
          grossSold: safeNumber(row.gross_sold_sum || row.gross_sales_sum),
          netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
          netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
          adCreatedTime: row.ad_created_time || null,
        };

        const derived = computeDerivedMetrics(baseRow);
        const maturityLabel = getMaturityLabel({
          ...baseRow,
          ...derived,
        });
        const adAgeDays = getAdAgeDays(baseRow.adCreatedTime, referenceDate);
        const creativeScore = getCreativeScore({
          ...baseRow,
          ...derived,
        });
        const businessScore = getBusinessScore({
          ...baseRow,
          ...derived,
        });
        const overallScore = getOverallScore(
          { ...baseRow, ...derived, maturityLabel },
          creativeScore,
          businessScore
        );

        return {
          ...baseRow,
          ...derived,
          adAgeDays,
          adAgeLabel: formatAdAge(adAgeDays),
          creativeScore,
          businessScore,
          overallScore,
          lifecycleLabel: getLifecycleLabel({
            ageDays: adAgeDays,
            spend: baseRow.spend,
            impressions: baseRow.impressions,
            leads: baseRow.leads,
            issues: baseRow.issues,
            demos: baseRow.demos,
            netSold: baseRow.netSold,
          }),
          maturityLabel,
          creativeVerdict: getCreativeVerdict(
            { ...baseRow, ...derived, maturityLabel },
            creativeScore,
            overallScore
          ),
          diagnosticLabel: getDiagnosticLabel(
            { ...baseRow, ...derived, maturityLabel },
            creativeScore,
            businessScore
          ),
        };
      })
      .sort((left, right) => right.spend - left.spend)
  ), [clientMetadataById]);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (loadingClientMeta) return;

      setLoading(true);
      setError(null);

      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ordering: '-total_spend_sum',
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
          ...(selectedCampaignTypes.length ? { campaign_type: selectedCampaignTypes.join(',') } : {}),
          ...(selectedVerticals.length ? { vertical: selectedVerticals.join(',') } : {}),
          ...(selectedThemes.length ? { theme: selectedThemes.join(',') } : {}),
          ...(selectedSubthemes.length ? { subtheme: selectedSubthemes.join(',') } : {}),
          ...(selectedAdTypes.length ? { ad_type: selectedAdTypes.join(',') } : {}),
          ...(selectedNotes.length ? { notes: selectedNotes.join(',') } : {}),
        };

        const recentStart = getRecentStartDate(startDate, endDate);
        const recentPayload = {
          ...payload,
          date_after: formatDateToYMD(recentStart),
          date_before: formatDateToYMD(endDate),
        };

        const [summaryResp, recentSummaryResp] = await Promise.all([
          getReportFetcher(viewMode)(payload),
          getReportFetcher(viewMode)(recentPayload),
        ]);

        const summaryRows = Array.isArray(summaryResp?.data)
          ? summaryResp.data
          : Array.isArray(summaryResp?.data?.results)
            ? summaryResp.data.results
            : [];
        const recentSummaryRows = Array.isArray(recentSummaryResp?.data)
          ? recentSummaryResp.data
          : Array.isArray(recentSummaryResp?.data?.results)
            ? recentSummaryResp.data.results
            : [];

        const nextRows = mapSummaryRows(summaryRows, endDate, viewMode);
        const nextRecentRows = mapSummaryRows(recentSummaryRows, endDate, viewMode);

        if (isMounted) {
          setRows(nextRows);
          setRecentRows(nextRecentRows);
        }
      } catch (err) {
        console.error('Failed to load ad performance aggregate:', err);
        if (isMounted) {
          setRows([]);
          setRecentRows([]);
          setError('Unable to load aggregated ad performance data.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [
    clientMetadataById,
    effectiveClientIds,
    endDate,
    loadingClientMeta,
    selectedAdTypes,
    selectedCampaignTypes,
    selectedNotes,
    selectedParentCompanies,
    selectedSubthemes,
    selectedThemes,
    selectedVerticals,
    startDate,
    viewMode,
    mapSummaryRows,
  ]);

  React.useEffect(() => {
    let isMounted = true;

    const loadFilterOptions = async () => {
      if (loadingClientMeta) return;

      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
        };
        const response = await fetchAdSummaryFilterOptions(payload);
        const data = response?.data || {};
        if (!isMounted) return;
        setFilterOptions({
          campaignTypes: toOptionObjects(data.campaign_types || []),
          verticals: toOptionObjects(data.verticals || []),
          themes: toOptionObjects(data.themes || []),
          subthemes: toOptionObjects(data.subthemes || []),
          adTypes: toOptionObjects(data.ad_types || []),
          notes: toOptionObjects(data.notes || []),
        });
      } catch (err) {
        console.error('Failed to load ad summary filter options:', err);
        if (!isMounted) return;
        setFilterOptions({
          campaignTypes: [],
          verticals: [],
          themes: [],
          subthemes: [],
          adTypes: [],
          notes: [],
        });
      }
    };

    loadFilterOptions();
    return () => {
      isMounted = false;
    };
  }, [effectiveClientIds, endDate, loadingClientMeta, selectedParentCompanies, startDate]);

  const filteredRows = React.useMemo(
    () =>
      rows.filter((row) => (
        meetsThreshold(row.spend, minSpend)
        && meetsThreshold(row.impressions, minImpressions)
        && meetsThreshold(row.leads, minLeads)
        && meetsThreshold(row.issues, minIssues)
        && meetsThreshold(row.demos, minDemos)
        && meetsThreshold(row.netSold, minNetSales)
      )),
    [rows, minSpend, minImpressions, minLeads, minIssues, minDemos, minNetSales]
  );

  const decisionRows = React.useMemo(() => {
    if (viewMode !== 'ad') {
      return filteredRows.map((row) => {
        const confidenceScore = getConfidenceScore(row);
        return {
          ...row,
          confidenceScore,
          confidenceLabel: getConfidenceLabel(confidenceScore),
          spendShareLabel: '-',
          peerRankLabel: '-',
          stabilityLabel: '-',
          trendLabel: '-',
          whyLabel: 'Grouped summary row.',
        };
      });
    }

    const recentRowsByUuid = new Map(recentRows.map((row) => [row.uuid, row]));

    return filteredRows.map((row) => {
      const primaryKpi = getPrimaryKpi(row);
      const recentRow = recentRowsByUuid.get(row.uuid) || row;
      const recentPrimaryKpi = getPrimaryKpi(recentRow);
      const clientVerticalPeers = filteredRows.filter(
        (peer) => (
          peer.clientId === row.clientId
          && peer.verticalName === row.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== row.uuid
        )
      );
      const agencyVerticalPeers = filteredRows.filter(
        (peer) => (
          peer.verticalName === row.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== row.uuid
        )
      );
      const backendClientVerticalPeers = clientVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const backendAgencyVerticalPeers = agencyVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const clientBenchmarks = getFunnelBenchmarks(backendClientVerticalPeers);
      const agencyBenchmarks = getFunnelBenchmarks(backendAgencyVerticalPeers);
      const selectedBenchmarks = hasUsableBenchmarks(clientBenchmarks)
        ? clientBenchmarks
        : agencyBenchmarks;
      const predictedCostOfMarketing = row.netSold >= 10
        ? null
        : getPredictedCostOfMarketing(row, selectedBenchmarks);
      const effectiveCostOfMarketing = row.netSold >= 10 && row.costOfMarketing > 0
        ? row.costOfMarketing
        : predictedCostOfMarketing;
      const decisionCostKpi = effectiveCostOfMarketing > 0
        ? {
            key: 'effectiveCostOfMarketing',
            label: row.netSold >= 10 ? 'Cost of Marketing' : 'Predicted Cost of Marketing',
            better: 'lower',
            benchmarkScope: 'client_vertical',
            isEligible: (candidate) => safeNumber(candidate.effectiveCostOfMarketing) > 0,
          }
        : primaryKpi;
      const enrichedClientVerticalPeers = clientVerticalPeers.map((peer) => ({
        ...peer,
        effectiveCostOfMarketing: peer.netSold >= 10 && peer.costOfMarketing > 0
          ? peer.costOfMarketing
          : getPredictedCostOfMarketing(peer, selectedBenchmarks),
      }));
      const enrichedAgencyVerticalPeers = agencyVerticalPeers.map((peer) => ({
        ...peer,
        effectiveCostOfMarketing: peer.netSold >= 10 && peer.costOfMarketing > 0
          ? peer.costOfMarketing
          : getPredictedCostOfMarketing(peer, selectedBenchmarks),
      }));
      const clientVerticalDelta = getBenchmarkDelta(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        enrichedClientVerticalPeers,
        decisionCostKpi
      );
      const agencyVerticalDelta = getBenchmarkDelta(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        enrichedAgencyVerticalPeers,
        decisionCostKpi
      );
      const benchmarkDelta = decisionCostKpi?.benchmarkScope === 'agency_vertical'
        ? agencyVerticalDelta
        : clientVerticalDelta;

      const recentClientVerticalPeers = recentRows.filter(
        (peer) => (
          peer.clientId === recentRow.clientId
          && peer.verticalName === recentRow.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== recentRow.uuid
        )
      );
      const recentAgencyVerticalPeers = recentRows.filter(
        (peer) => (
          peer.verticalName === recentRow.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== recentRow.uuid
        )
      );
      const recentBackendClientVerticalPeers = recentClientVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const recentBackendAgencyVerticalPeers = recentAgencyVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const recentClientBenchmarks = getFunnelBenchmarks(recentBackendClientVerticalPeers);
      const recentAgencyBenchmarks = getFunnelBenchmarks(recentBackendAgencyVerticalPeers);
      const recentSelectedBenchmarks = hasUsableBenchmarks(recentClientBenchmarks)
        ? recentClientBenchmarks
        : recentAgencyBenchmarks;
      const recentPredictedCostOfMarketing = recentRow.netSold >= 10
        ? null
        : getPredictedCostOfMarketing(recentRow, recentSelectedBenchmarks);
      const recentEffectiveCostOfMarketing = recentRow.netSold >= 10 && recentRow.costOfMarketing > 0
        ? recentRow.costOfMarketing
        : recentPredictedCostOfMarketing;
      const recentDecisionCostKpi = recentEffectiveCostOfMarketing > 0
        ? {
            key: 'effectiveCostOfMarketing',
            label: recentRow.netSold >= 10 ? 'Cost of Marketing' : 'Predicted Cost of Marketing',
            better: 'lower',
            benchmarkScope: 'client_vertical',
            isEligible: (candidate) => safeNumber(candidate.effectiveCostOfMarketing) > 0,
          }
        : recentPrimaryKpi;
      const enrichedRecentClientVerticalPeers = recentClientVerticalPeers.map((peer) => ({
        ...peer,
        effectiveCostOfMarketing: peer.netSold >= 10 && peer.costOfMarketing > 0
          ? peer.costOfMarketing
          : getPredictedCostOfMarketing(peer, recentSelectedBenchmarks),
      }));
      const enrichedRecentAgencyVerticalPeers = recentAgencyVerticalPeers.map((peer) => ({
        ...peer,
        effectiveCostOfMarketing: peer.netSold >= 10 && peer.costOfMarketing > 0
          ? peer.costOfMarketing
          : getPredictedCostOfMarketing(peer, recentSelectedBenchmarks),
      }));
      const recentClientVerticalDelta = getBenchmarkDelta(
        {
          ...recentRow,
          effectiveCostOfMarketing: recentEffectiveCostOfMarketing,
        },
        enrichedRecentClientVerticalPeers,
        recentDecisionCostKpi
      );
      const recentAgencyVerticalDelta = getBenchmarkDelta(
        {
          ...recentRow,
          effectiveCostOfMarketing: recentEffectiveCostOfMarketing,
        },
        enrichedRecentAgencyVerticalPeers,
        recentDecisionCostKpi
      );
      const recentBenchmarkDelta = recentDecisionCostKpi?.benchmarkScope === 'agency_vertical'
        ? recentAgencyVerticalDelta
        : recentClientVerticalDelta;

      const clientVerticalTotalSpend = filteredRows
        .filter((peer) => (
          peer.clientId === row.clientId
          && peer.verticalName === row.verticalName
          && peer.campaignType === row.campaignType
        ))
        .reduce((sum, peer) => sum + safeNumber(peer.spend), 0);
      const spendShare = safeDiv(row.spend, clientVerticalTotalSpend);

      const confidenceScore = getConfidenceScore(row);
      const confidenceLabel = getConfidenceLabel(confidenceScore);
      const trendLabel = getTrendLabel(
        benchmarkDelta,
        recentBenchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );

      const creativeBenchmarkDelta = getBenchmarkDelta(
        row,
        agencyVerticalPeers,
        {
          key: 'ctr',
          label: 'CTR',
          better: 'higher',
          benchmarkScope: 'agency_vertical',
          isEligible: (candidate) => candidate.impressions > 0,
        }
      );
      const businessBenchmarkDelta = primaryKpi?.benchmarkScope === 'agency_vertical'
        ? agencyVerticalDelta
        : clientVerticalDelta;

      const creativeScore = getBenchmarkAdjustedScore(row.creativeScore, creativeBenchmarkDelta, 'higher');
      const businessScore = getBenchmarkAdjustedScore(
        getBusinessScore({
          ...row,
          effectiveCostOfMarketing,
        }),
        businessBenchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );
      const overallScore = clampScore((creativeScore * 0.4) + (businessScore * 0.6));
      const creativeVerdict = getCreativeVerdict(
        { ...row, creativeScore, businessScore, overallScore },
        creativeScore,
        overallScore
      );
      const diagnosticLabel = getDiagnosticLabel(
        { ...row, creativeScore, businessScore, overallScore },
        creativeScore,
        businessScore
      );

      const benchmarkScope = decisionCostKpi?.benchmarkScope === 'agency_vertical'
        ? 'agency_vertical'
        : 'client_vertical';
      const overallRecommendation = getRecommendation(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        decisionCostKpi,
        benchmarkDelta
      );
      const whyLabel = getWhyLabel({
        row,
        primaryKpi: decisionCostKpi || primaryKpi,
        benchmarkDelta,
        benchmarkScope,
        recommendation: overallRecommendation,
        diagnosticLabel,
        maturityLabel: row.maturityLabel,
        spendShare,
        confidenceLabel,
      });
      const recentRecommendation = getRecommendation(
        {
          ...recentRow,
          effectiveCostOfMarketing: recentEffectiveCostOfMarketing,
        },
        recentDecisionCostKpi || recentPrimaryKpi,
        recentBenchmarkDelta
      );
      const peerRowsForRank = benchmarkScope === 'agency_vertical'
        ? enrichedAgencyVerticalPeers
        : enrichedClientVerticalPeers;
      const peerRankLabel = getPeerRankLabel(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        peerRowsForRank,
        decisionCostKpi || primaryKpi
      );
      const stabilityLabel = getStabilityLabel(recentRecommendation, overallRecommendation, trendLabel);
      const directionalDelta = getDirectionalDelta(
        benchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );
      const pausePriority = getPausePriority({
        overallRecommendation,
        recentRecommendation,
        creativeVerdict,
        diagnosticLabel,
        confidenceScore,
        spendShare,
        directionalDelta,
      });
      const buildPriority = getBuildPriority({
        overallRecommendation,
        recentRecommendation,
        creativeVerdict,
        confidenceScore,
        spendShare,
        directionalDelta,
        stageLabel: row.maturityLabel,
      });

      return {
        ...row,
        stageLabel: row.maturityLabel,
        creativeScore,
        businessScore,
        overallScore,
        creativeVerdict,
        diagnosticLabel,
        predictedCostOfMarketing,
        predictedCostOfMarketingLabel: predictedCostOfMarketing == null ? '-' : formatPercentLabel(predictedCostOfMarketing),
        effectiveCostOfMarketing,
        effectiveCostOfMarketingLabel: effectiveCostOfMarketing == null ? '-' : formatPercentLabel(effectiveCostOfMarketing),
        confidenceScore,
        confidenceLabel,
        spendShare,
        spendShareLabel: formatPercentLabel(spendShare),
        pausePriority,
        pausePriorityLabel: formatPriorityLabel(pausePriority),
        buildPriority,
        buildPriorityLabel: formatPriorityLabel(buildPriority),
        peerRankLabel,
        stabilityLabel,
        primaryKpiLabel: primaryKpi?.label || '-',
        clientVerticalDelta,
        agencyVerticalDelta,
        clientVerticalDeltaLabel: formatDeltaLabel(clientVerticalDelta),
        agencyVerticalDeltaLabel: formatDeltaLabel(agencyVerticalDelta),
        decisionBenchmarkDelta: benchmarkDelta,
        recentRecommendation,
        overallRecommendation,
        trendLabel,
        whyLabel,
      };
    });
  }, [filteredRows, recentRows, viewMode]);

  const totals = React.useMemo(() => {
    const base = decisionRows.reduce(
      (acc, row) => {
        acc.spend += safeNumber(row.spend);
        acc.leads += safeNumber(row.leads);
        acc.selfBook += safeNumber(row.selfBook);
        acc.booked += safeNumber(row.booked);
        acc.scheduled += safeNumber(row.scheduled);
        acc.issues += safeNumber(row.issues);
        acc.demos += safeNumber(row.demos);
        acc.grossSold += safeNumber(row.grossSold);
        acc.netSold += safeNumber(row.netSold);
        acc.netAmount += safeNumber(row.netAmount);
        return acc;
      },
      {
        spend: 0,
        leads: 0,
        selfBook: 0,
        booked: 0,
        scheduled: 0,
        issues: 0,
        demos: 0,
        grossSold: 0,
        netSold: 0,
        netAmount: 0,
      }
    );
    return computeDerivedMetrics(base);
  }, [decisionRows]);

  const selectedKpiOption = React.useMemo(
    () => KPI_OPTIONS.find((option) => option.key === kpiMetric) || KPI_OPTIONS[0],
    [kpiMetric]
  );

  const topPerformers = React.useMemo(
    () => getRankedRows(decisionRows, selectedKpiOption, 'best'),
    [decisionRows, selectedKpiOption]
  );

  const bottomPerformers = React.useMemo(
    () => getRankedRows(decisionRows, selectedKpiOption, 'worst'),
    [decisionRows, selectedKpiOption]
  );

  const bestThemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'themeName').sort((a, b) => b.overallScore - a.overallScore).slice(0, 5),
    [decisionRows]
  );

  const worstThemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'themeName').sort((a, b) => a.overallScore - b.overallScore).slice(0, 5),
    [decisionRows]
  );

  const bestSubthemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'subthemeName').sort((a, b) => b.overallScore - a.overallScore).slice(0, 5),
    [decisionRows]
  );

  const worstSubthemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'subthemeName').sort((a, b) => a.overallScore - b.overallScore).slice(0, 5),
    [decisionRows]
  );

  const bestFormats = React.useMemo(
    () => getBestComponentLeaders(decisionRows, 'facebookFormat'),
    [decisionRows]
  );

  const bestAdTypes = React.useMemo(
    () => getBestComponentLeaders(decisionRows, 'adType'),
    [decisionRows]
  );

  const actionQueues = React.useMemo(() => {
    if (viewMode !== 'ad') {
      return {
        topKeepRunning: [],
        topPause: [],
        keepTesting: [],
        buildMore: [],
      };
    }
    const rankedByPause = [...decisionRows].sort((a, b) => (
      b.pausePriority - a.pausePriority || b.spend - a.spend
    ));
    const rankedByBuild = [...decisionRows].sort((a, b) => (
      b.buildPriority - a.buildPriority || b.spend - a.spend
    ));
    const buildMore = rankedByBuild
      .filter((row) => row.buildPriority >= 45 && row.creativeVerdict !== 'Weak')
      .slice(0, 5);

    return {
      topKeepRunning: rankedByBuild
        .filter((row) => row.overallRecommendation === 'Keep Running' && row.buildPriority >= 35)
        .slice(0, 5),
      topPause: rankedByPause
        .filter((row) => row.pausePriority >= 35)
        .slice(0, 5),
      keepTesting: rankedByBuild
        .filter((row) => row.overallRecommendation === 'Test Longer' || row.confidenceLabel === 'Low')
        .slice(0, 5),
      buildMore,
    };
  }, [decisionRows, viewMode]);

  const dimensionColumns = React.useMemo(() => getDimensionColumns(viewMode), [viewMode]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  const handleOpenPreview = React.useCallback((row) => {
    setPreviewRow(row);
  }, []);

  const handleClosePreview = React.useCallback(() => {
    setPreviewRow(null);
  }, []);

  const pageSubheader = {
    ad: 'Broad campaigns work best at the ad level. Creative evaluation combines Facebook delivery, lead progression, and business-outcome guidance. Clients without backend integrations stop at lead/self-book stages and are excluded from later-stage benchmark math.',
    theme: 'Theme-level creative pattern rollup. This is especially useful for local/county campaigns where ad-level data is too fragmented.',
    subtheme: 'Subtheme-level creative pattern rollup. This is the preferred view for local/county campaigns and grouped creative decisions.',
  }[viewMode];

  const normalizedCampaignLens = React.useMemo(
    () => (selectedCampaignTypes.length ? selectedCampaignTypes.map(normalizeCampaignType) : ['Broad', 'Local']),
    [selectedCampaignTypes]
  );

  const isLocalOnlyLens = normalizedCampaignLens.length === 1 && normalizedCampaignLens[0] === 'Local';
  const isBroadOnlyLens = normalizedCampaignLens.length === 1 && normalizedCampaignLens[0] === 'Broad';
  const lensLabel = isBroadOnlyLens ? 'Broad' : isLocalOnlyLens ? 'Local / County' : 'Mixed';
  const backendCapableRows = decisionRows.filter((row) => row.hasBackendIntegration).length;
  const parsedThemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((row) => row.themeName).length, decisionRows.length) : 0;
  const parsedSubthemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((row) => row.subthemeName).length, decisionRows.length) : 0;
  const showAdDecisionBoard = viewMode === 'ad' && !isLocalOnlyLens;
  const showGroupedDecisionBoard = viewMode !== 'ad' || isLocalOnlyLens;

  return (
    <Page title="Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardHeader
            title="Creative Performance"
            subheader={`${pageSubheader} Use the mapping switch to include clients with mappings, without mappings, or all clients.`}
          />
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  style={{ width: '100%' }}
                />
              </Box>

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 240 }}
                options={parentCompanyOptions.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
                value={parentCompanyOptions
                  .filter((option) => selectedParentCompanies.includes(option.id))
                  .map((option) => ({ label: option.name, value: option.id }))}
                onChange={(_, value) => setSelectedParentCompanies(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Parent Companies" />}
              />

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 240 }}
                options={clientOptions.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
                value={clientOptions
                  .filter((option) => selectedClients.includes(option.id))
                  .map((option) => ({ label: option.name, value: option.id }))}
                onChange={(_, value) => setSelectedClients(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Clients" />}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <ToggleButtonGroup
                value={mappingMode}
                exclusive
                size="small"
                onChange={(_, value) => {
                  if (value) setMappingMode(value);
                }}
              >
                {MAPPING_MODE_OPTIONS.map((option) => (
                  <ToggleButton key={option.key} value={option.key}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                size="small"
                onChange={(_, value) => {
                  if (value) setViewMode(value);
                }}
              >
                {VIEW_MODE_OPTIONS.map((option) => (
                  <ToggleButton key={option.key} value={option.key}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={KPI_OPTIONS}
                value={selectedKpiOption}
                onChange={(_, value) => setKpiMetric(value?.key || KPI_OPTIONS[0].key)}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="KPI Focus" />}
              />

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 220 }}
                options={filterOptions.campaignTypes}
                value={filterOptions.campaignTypes.filter((option) => selectedCampaignTypes.includes(option.value))}
                onChange={(_, value) => setSelectedCampaignTypes(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Campaign Lens" />}
              />

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 220 }}
                options={filterOptions.verticals}
                value={filterOptions.verticals.filter((option) => selectedVerticals.includes(option.value))}
                onChange={(_, value) => setSelectedVerticals(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Vertical" />}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 220 }}
                options={filterOptions.themes}
                value={filterOptions.themes.filter((option) => selectedThemes.includes(option.value))}
                onChange={(_, value) => setSelectedThemes(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Theme" />}
              />

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 220 }}
                options={filterOptions.subthemes}
                value={filterOptions.subthemes.filter((option) => selectedSubthemes.includes(option.value))}
                onChange={(_, value) => setSelectedSubthemes(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Subtheme" />}
              />

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 220 }}
                options={filterOptions.adTypes}
                value={filterOptions.adTypes.filter((option) => selectedAdTypes.includes(option.value))}
                onChange={(_, value) => setSelectedAdTypes(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Ad Type" />}
              />

              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 220 }}
                options={filterOptions.notes}
                value={filterOptions.notes.filter((option) => selectedNotes.includes(option.value))}
                onChange={(_, value) => setSelectedNotes(value.map((option) => option.value))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => <TextField {...params} label="Notes" />}
              />

              <Typography variant="body2" color="text.secondary">
                Showing {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode === 'theme' ? 'themes' : 'subthemes'}
              </Typography>
              {selectedCampaignTypes.length === 1 && normalizeCampaignType(selectedCampaignTypes[0]) === 'Local' ? (
                <Typography variant="body2" color="text.secondary">
                  Local/county campaigns are best judged in grouped theme/subtheme views.
                </Typography>
              ) : null}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
              <TextField
                size="small"
                label="Min Spend"
                type="number"
                value={minSpend}
                onChange={(event) => setMinSpend(event.target.value)}
                sx={{ maxWidth: 140 }}
              />
              <TextField
                size="small"
                label="Min Impr."
                type="number"
                value={minImpressions}
                onChange={(event) => setMinImpressions(event.target.value)}
                sx={{ maxWidth: 140 }}
              />
              <TextField
                size="small"
                label="Min Leads"
                type="number"
                value={minLeads}
                onChange={(event) => setMinLeads(event.target.value)}
                sx={{ maxWidth: 140 }}
              />
              <TextField
                size="small"
                label="Min Issues"
                type="number"
                value={minIssues}
                onChange={(event) => setMinIssues(event.target.value)}
                sx={{ maxWidth: 140 }}
              />
              <TextField
                size="small"
                label="Min Demos"
                type="number"
                value={minDemos}
                onChange={(event) => setMinDemos(event.target.value)}
                sx={{ maxWidth: 140 }}
              />
              <TextField
                size="small"
                label="Min Net Sales"
                type="number"
                value={minNetSales}
                onChange={(event) => setMinNetSales(event.target.value)}
                sx={{ maxWidth: 150 }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          <Card sx={{ flex: 1 }}>
            <CardHeader
              title="Creative Command"
              subheader="Fast read on the current scope before you dive into the decision queues."
            />
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    color: 'common.white',
                    background: isLocalOnlyLens
                      ? 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)'
                      : 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
                  }}
                >
                  <Typography variant="overline" sx={{ opacity: 0.85 }}>
                    Active Lens
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {lensLabel}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                    {showAdDecisionBoard
                      ? 'Use ad-level decisions for pause, keep-running, and build-more actions.'
                      : 'Use grouped theme/subtheme patterns for local and fragmented campaign analysis.'}
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                    <Typography variant="overline" color="text.secondary">Spend</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                    <Typography variant="body2" color="text.secondary">Across {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode}.</Typography>
                  </Box>
                  <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                    <Typography variant="overline" color="text.secondary">Backend Coverage</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{backendCapableRows}</Typography>
                    <Typography variant="body2" color="text.secondary">Rows with downstream benchmark eligibility.</Typography>
                  </Box>
                  <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                    <Typography variant="overline" color="text.secondary">Theme Coverage</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatPercentLabel(parsedThemeCoverage)}</Typography>
                    <Typography variant="body2" color="text.secondary">Subtheme coverage: {formatPercentLabel(parsedSubthemeCoverage)}</Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardHeader
              title="Decision Focus"
              subheader="The main question this view is optimized to answer right now."
            />
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="overline" color="text.secondary">Primary Question</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                    {showAdDecisionBoard
                      ? 'Which broad ads should stay on, which should pause, and which winners should inspire the next build?'
                      : 'Which local themes and subthemes are strong enough to keep producing and which grouped patterns should be deprioritized?'}
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="overline" color="text.secondary">Current KPI Focus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                    {selectedKpiOption.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isLocalOnlyLens
                      ? 'Local/county rows should usually be interpreted at grouped theme/subtheme level, not one ad at a time.'
                      : 'Broad ads can be judged ad by ad, while theme/subtheme cards still help with client, vertical, and agency-wide creative direction.'}
                  </Typography>
                </Box>
                <Stack spacing={1.25}>
                  <Typography variant="overline" color="text.secondary">Fast Signals</Typography>
                  {topPerformers.slice(0, 2).map((row) => (
                    <Box key={`signal-top-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography>
                      <Typography sx={{ fontWeight: 700 }}>
                        {renderMetricValue(
                          row[selectedKpiOption.key],
                          METRIC_COLUMNS.find((column) => column.key === selectedKpiOption.key)?.type || 'count'
                        )}
                      </Typography>
                    </Box>
                  ))}
                  {bottomPerformers.slice(0, 2).map((row) => (
                    <Box key={`signal-bottom-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography>
                      <Typography sx={{ fontWeight: 700, color: 'error.main' }}>
                        {renderMetricValue(
                          row[selectedKpiOption.key],
                          METRIC_COLUMNS.find((column) => column.key === selectedKpiOption.key)?.type || 'count'
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          {showAdDecisionBoard ? (
            <React.Fragment>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Top Keep-Running Ads" subheader="Best current ads to leave on based on decision CoM, confidence, and meaningful spend." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.topKeepRunning.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No clear keep-running ads.</Typography>
                    ) : actionQueues.topKeepRunning.map((row) => (
                      <Box key={`keep-running-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.verticalName || 'Unassigned'} • {row.spendShareLabel} share
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.overallRecommendation}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Top Pause Candidates" subheader="Highest wasted-spend risk based on weak signal, confidence, and spend share." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.topPause.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No clear pause candidates.</Typography>
                    ) : actionQueues.topPause.map((row) => (
                      <Box key={`pause-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.verticalName || 'Unassigned'} • {row.spendShareLabel} share
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.pausePriorityLabel}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Keep Testing" subheader="Ads with interesting signal but not enough confidence yet to force a decision." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.keepTesting.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No ads currently in test-longer status.</Typography>
                    ) : actionQueues.keepTesting.map((row) => (
                      <Box key={`test-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.verticalName || 'Unassigned'} • {row.confidenceLabel} confidence
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.overallRecommendation}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Build More Like This" subheader="Creative patterns worth reusing when you brief the next round of ads." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.buildMore.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No strong build-more patterns yet.</Typography>
                    ) : actionQueues.buildMore.map((row) => (
                      <Box key={`build-more-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.themeName || 'No Theme'} • {row.subthemeName || 'No Subtheme'} • {row.facebookFormat || 'Unknown'}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.buildPriorityLabel}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </React.Fragment>
          ) : null}
        </Stack>

        {showGroupedDecisionBoard ? (
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
            <Card sx={{ flex: 1 }}>
              <CardHeader title="Best Grouped Winners" subheader="Top grouped patterns by the current KPI focus in the active scope." />
              <CardContent>
                <Stack spacing={1.5}>
                  {topPerformers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No grouped winners yet.</Typography>
                  ) : topPerformers.map((row) => (
                    <Box key={`group-win-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.clientCount ? `${row.clientCount} clients • ` : ''}<DollarFormatter value={row.spend} />
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {renderMetricValue(
                          row[selectedKpiOption.key],
                          METRIC_COLUMNS.find((column) => column.key === selectedKpiOption.key)?.type || 'count'
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardHeader title="Grouped Pause Risks" subheader="Patterns that look weak enough to stop producing or deprioritize." />
              <CardContent>
                <Stack spacing={1.5}>
                  {bottomPerformers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No grouped pause risks yet.</Typography>
                  ) : bottomPerformers.map((row) => (
                    <Box key={`group-risk-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.clientCount ? `${row.clientCount} clients • ` : ''}<DollarFormatter value={row.spend} />
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: 'error.main' }}>
                        {renderMetricValue(
                          row[selectedKpiOption.key],
                          METRIC_COLUMNS.find((column) => column.key === selectedKpiOption.key)?.type || 'count'
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          <Card sx={{ flex: 1 }}>
            <CardHeader title="Best Themes" subheader="Top parsed themes worth reusing in the current scope." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestThemes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No parsed theme data yet.</Typography>
                ) : bestThemes.map((theme) => (
                  <Box key={`best-theme-${theme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{theme.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {theme.count} ads • <DollarFormatter value={theme.spend} />
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{theme.overallScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardHeader title="Best Formats" subheader="Best creative formats to keep using when theme parsing is sparse." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestFormats.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No format data yet.</Typography>
                ) : bestFormats.map((format) => (
                  <Box key={`best-format-${format.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{format.name || 'Unknown'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {format.count} ads • <DollarFormatter value={format.spend} />
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{format.overallScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          <Card sx={{ flex: 1 }}>
            <CardHeader title="Best Subthemes" subheader="Top parsed subthemes worth briefing into the next round of ads." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestSubthemes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No parsed subtheme data yet.</Typography>
                ) : bestSubthemes.map((subtheme) => (
                  <Box key={`best-subtheme-${subtheme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{subtheme.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subtheme.count} ads • <DollarFormatter value={subtheme.spend} />
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{subtheme.overallScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardHeader title="Best Ad Types" subheader="Best-performing ad types to reuse in new static and video builds." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestAdTypes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No ad type data yet.</Typography>
                ) : bestAdTypes.map((adType) => (
                  <Box key={`best-ad-type-${adType.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{adType.name || 'Unknown'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {adType.count} ads • <DollarFormatter value={adType.spend} />
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{adType.overallScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Card>
          <CardHeader
            title={
              viewMode === 'ad'
                ? 'Creative Evaluation Table'
                : viewMode === 'theme'
                  ? 'Theme Evaluation Table'
                  : 'Subtheme Evaluation Table'
            }
            subheader="Decision-first creative evaluation with spend, funnel progression, and business outcome metrics."
          />
          <CardContent sx={{ p: 0 }}>
            {loading || loadingClientMeta ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: '74vh' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 2400 }}>
                  <TableHead>
                    <TableRow>
                      {dimensionColumns.map((column, index) => (
                        <TableCell
                          key={column.key}
                          sx={{
                            minWidth: column.minWidth,
                            ...(column.sticky
                              ? {
                                  position: 'sticky',
                                  left: column.stickyLeft || 0,
                                  zIndex: 3,
                                  backgroundColor: 'background.paper',
                                }
                              : {}),
                          }}
                        >
                          {column.helpText ? (
                            <Tooltip title={column.helpText} arrow>
                              <Box component="span" sx={{ cursor: 'help' }}>
                                {column.label}
                              </Box>
                            </Tooltip>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                      {METRIC_COLUMNS.map((column) => (
                        <TableCell key={column.key} sx={{ minWidth: 120 }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {decisionRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={METRIC_COLUMNS.length + dimensionColumns.length} align="center" sx={{ py: 6 }}>
                          No rows match the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {decisionRows.map((row) => (
                          <TableRow key={row.uuid} hover>
                            {dimensionColumns.map((column, index) => (
                              <TableCell
                                key={`${row.uuid}-${column.key}`}
                                title={column.truncate ? (row[column.key] || '') : undefined}
                                sx={
                                  column.sticky
                                    ? {
                                        position: 'sticky',
                                        left: column.stickyLeft || 0,
                                        zIndex: 2,
                                        backgroundColor: 'background.paper',
                                      }
                                    : undefined
                                }
                            >
                                {renderDimensionValue(column, row, handleOpenPreview)}
                              </TableCell>
                            ))}
                            {METRIC_COLUMNS.map((column) => (
                              <TableCell key={column.key}>
                                {renderMetricValue(row[column.key], column.type)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow>
                          {dimensionColumns.map((column, index) => (
                            <TableCell
                              key={`total-${column.key}`}
                              sx={{
                                fontWeight: 700,
                                ...(column.sticky
                                  ? {
                                      position: 'sticky',
                                      left: column.stickyLeft || 0,
                                      zIndex: 2,
                                      backgroundColor: 'background.paper',
                                    }
                                  : {}),
                              }}
                            >
                              {index === 0 ? 'Total' : '-'}
                            </TableCell>
                          ))}
                          {METRIC_COLUMNS.map((column) => (
                            <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                              {renderMetricValue(totals[column.key], column.type)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Dialog open={Boolean(previewRow)} onClose={handleClosePreview} maxWidth="md" fullWidth>
          <DialogTitle>{previewRow?.adName || 'Ad Preview'}</DialogTitle>
          <DialogContent>
            {previewRow ? (
              <Stack spacing={2.5}>
                {previewRow.imageUrl || previewRow.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={previewRow.imageUrl || previewRow.thumbnailUrl}
                    alt={previewRow.adName || 'Ad creative preview'}
                    sx={{
                      width: '100%',
                      maxHeight: 360,
                      objectFit: 'contain',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                    }}
                  />
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap">
                  {renderStatusPill(previewRow.facebookFormat || 'Unknown', getFormatTone(previewRow.facebookFormat))}
                  {renderStatusPill(previewRow.creativeVerdict || 'Unknown', getVerdictTone(previewRow.creativeVerdict))}
                  {renderStatusPill(previewRow.overallRecommendation || 'Unknown', getRecommendationTone(previewRow.overallRecommendation))}
                  {previewRow.ctaType ? renderStatusPill(previewRow.ctaType, 'neutral') : null}
                </Stack>
                <Box>
                  <Typography variant="overline" color="text.secondary">Headline</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {previewRow.headline || 'No headline stored.'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">Primary Text</Typography>
                  <Typography variant="body2">
                    {previewRow.primaryText || 'No primary text stored.'}
                  </Typography>
                </Box>
                {previewRow.description ? (
                  <Box>
                    <Typography variant="overline" color="text.secondary">Description</Typography>
                    <Typography variant="body2">{previewRow.description}</Typography>
                  </Box>
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Why</Typography>
                    <Typography variant="body2">{previewRow.whyLabel || '-'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Destination</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {previewRow.destinationUrl || 'No destination URL stored.'}
                    </Typography>
                  </Box>
                </Stack>
                {previewRow.videoId ? (
                  <Typography variant="body2" color="text.secondary">
                    Stored video id: {previewRow.videoId}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </DialogContent>
        </Dialog>
      </Stack>
    </Page>
  );
}
